import * as fs from 'fs';
import * as path from 'path';
import type { Survey } from '../fetchSurvey/types/survey';
import { colors } from './const';
import { fetchSurveyFromEnv, extractSurveyTypesFromSurvey } from './fetch-survey-inventory';
import {
  extractComponentInventory,
  extractCustomFieldInventory,
  extractFrameworkSection,
  extractLogicInventory,
  extractMatrixVariants,
  extractPlaceholderTokens,
  formatComponentTableRows,
  formatCustomFieldTableRows,
  formatLogicTableRows,
  formatMatrixTableRows,
  formatPlaceholderTableRows,
  surveyHasMcqOtherOption,
  surveyHasMcqOtherPickerTypes,
} from './survey-inventory-extract';
import { printSurveyTypeInstallBlock } from './install-manifest';

const c = colors;

function replaceSectionTable(
  content: string,
  sectionHeading: string,
  tableHeaderPattern: RegExp,
  newRows: string,
): string {
  const headingIdx = content.indexOf(sectionHeading);
  if (headingIdx < 0) return content;

  const afterHeading = content.slice(headingIdx);
  const headerMatch = afterHeading.match(tableHeaderPattern);
  if (!headerMatch || headerMatch.index === undefined) return content;

  const tableStart = headingIdx + headerMatch.index + headerMatch[0].length;
  const rest = content.slice(tableStart);
  const nextSection = rest.search(/\n## |\n##\d/);
  const tableBodyEnd = nextSection >= 0 ? tableStart + nextSection : content.length;

  const before = content.slice(0, tableStart);
  const after = content.slice(tableBodyEnd);
  const rows = newRows.endsWith('\n') ? newRows : `${newRows}\n`;
  return `${before}\n${rows}${after}`;
}

function patchFrameworkSection(content: string, projectRoot: string): string {
  const { frameworkLine, packageLine } = extractFrameworkSection(projectRoot);
  let out = content.replace(
    /- \*\*Framework:\*\* \[ \] Next\.js App Router.*\n/,
    `${frameworkLine}\n`,
  );
  out = out.replace(
    /- \*\*Package import name:\*\*.*\n/,
    `${packageLine}\n`,
  );
  return out;
}

export function patchImplementationPlan(
  content: string,
  projectRoot: string,
  survey: Survey,
): string {
  let out = patchFrameworkSection(content, projectRoot);

  out = replaceSectionTable(
    out,
    '## 3b. Required Component Files (from fetched survey)',
    /\| Component file \| Required if survey contains \| Created\? \|\n\|[-| ]+\|\n/,
    formatComponentTableRows(extractComponentInventory(survey)),
  );

  out = replaceSectionTable(
    out,
    '## 3c. Matrix variant inventory (from fetched survey)',
    /\| questionId \| type \| displayStyle \| statementLayout \| transposeTable \| selectionMode \| Labels visible\? \|\n\|[-| ]+\|\n/,
    formatMatrixTableRows(extractMatrixVariants(survey)),
  );

  const mcqOtherLine = `- **MCQ Other option in fetch:** ${surveyHasMcqOtherOption(survey) ? 'yes' : 'no'}`;
  const mcqOtherPickersLine = `- **MCQ Other phone/postal/date pickers:** ${surveyHasMcqOtherPickerTypes(survey) ? 'yes — include react-international-phone react-datepicker date-fns in Phase 1 install' : 'no'}`;
  if (out.includes('**MCQ Other option in fetch:**')) {
    out = out.replace(/- \*\*MCQ Other option in fetch:\*\*.*\n/, `${mcqOtherLine}\n`);
  } else {
    out = out.replace(/(- \*\*instanceId:\*\*.*\n)/, `$1${mcqOtherLine}\n`);
  }
  if (out.includes('**MCQ Other phone/postal/date pickers:**')) {
    out = out.replace(
      /- \*\*MCQ Other phone\/postal\/date pickers:\*\*.*\n/,
      `${mcqOtherPickersLine}\n`,
    );
  } else {
    out = out.replace(
      /(- \*\*MCQ Other option in fetch:\*\*.*\n)/,
      `$1${mcqOtherPickersLine}\n`,
    );
  }

  out = replaceSectionTable(
    out,
    '## 3d. Custom field inventory (mandatory when `_c_…` ids found)',
    /\| `_c_` id \| fieldType \| used in \(skip\/display\/answer\/end\/copy token\) \| logic field \(e\.g\. CASE_SENSITIVE_TEXT\) \| CRM value \| populated in `lib\/customFieldValues\.ts`\? \|\n\|[-| ]+\|\n/,
    formatCustomFieldTableRows(extractCustomFieldInventory(survey)),
  );

  out = replaceSectionTable(
    out,
    '## 3d-b. Placeholder inventory (from fetched question text)',
    /\| Token in fetch \| Key in map \| Example value \| Question ids \|\n\|[-| ]+\|\n/,
    formatPlaceholderTableRows(extractPlaceholderTokens(survey)),
  );

  out = replaceSectionTable(
    out,
    '## 3e. Logic inventory (from fetched survey)',
    /\| questionId \| skipLogics \| displayLogic \| answerLogic \(options\/rows\/cols\) \| customField refs \(`_c_…`\) \|\n\|[-| ]+\|\n/,
    formatLogicTableRows(extractLogicInventory(survey)),
  );

  if (!out.includes('<!-- autofill:from-fetch -->')) {
    out = out.replace(
      '# Survey UI Implementation Plan\n',
      '# Survey UI Implementation Plan\n\n<!-- autofill:from-fetch -->\n',
    );
  }

  return out;
}

export async function runFillImplementationPlan(options: {
  projectRoot?: string;
  instanceIdOverride?: string;
  planPath?: string;
}): Promise<{ planPath: string; surveyTypes: string[]; hasMcqOtherPickers: boolean }> {
  const projectRoot = options.projectRoot ?? process.cwd();
  const planPath = options.planPath ?? path.join(projectRoot, 'implementation_plan.md');

  if (!fs.existsSync(planPath)) {
    throw new Error(
      `fill-implementation-plan: ${planPath} not found — copy survey-ui/implementation_plan.md to project root first`,
    );
  }

  const { survey } = await fetchSurveyFromEnv(projectRoot, options.instanceIdOverride);
  const surveyTypes = extractSurveyTypesFromSurvey(survey);
  const original = fs.readFileSync(planPath, 'utf8');
  const patched = patchImplementationPlan(original, projectRoot, survey);
  fs.writeFileSync(planPath, patched, 'utf8');
  return {
    planPath,
    surveyTypes,
    hasMcqOtherPickers: surveyHasMcqOtherPickerTypes(survey),
  };
}

export async function fillImplementationPlanFromFetch(options: {
  instanceIdOverride?: string;
  projectRoot?: string;
}): Promise<void> {
  const projectRoot = options.projectRoot ?? process.cwd();
  const { planPath, surveyTypes, hasMcqOtherPickers } = await runFillImplementationPlan({
    instanceIdOverride: options.instanceIdOverride,
    projectRoot,
  });
  console.log(`${c.green}✅ Patched ${planPath} from live fetch (§0, §3b–§3e)${c.reset}`);
  printSurveyTypeInstallBlock(projectRoot, surveyTypes, hasMcqOtherPickers);
  console.log(`${c.dim}Per-type docs: on-demand (verify failure)${c.reset}`);
}
